import React from "react";

// The admin sidebar's tabs, grouped the way the sidebar shows them (founder
// pick 2026-10-31: the prototype's grouped look, with each icon in its own
// colour like the old emoji). Same tabs, ids and labels as before: only the
// order, the group headings and the icons changed. The phone tab strip uses
// the same list and order.

export type AdminTab =
  | "approvals" | "costs" | "ambassadors" | "overview" | "growth" | "users" | "fans" | "orders" | "admins"
  | "revenue" | "establishments" | "allPayouts" | "trending" | "disputes" | "paymentIssues" | "feedback"
  | "health" | "countries" | "guardrails";

export interface AdminNavTab {
  id: AdminTab;
  label: string;
  /** The icon's colour; the icon keeps it when the tab is active. */
  color: string;
  icon: React.ReactNode;
}

const icon = (paths: React.ReactNode) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {paths}
  </svg>
);

export const NAV_GROUPS: { title: string | null; tabs: AdminNavTab[] }[] = [
  {
    title: null,
    tabs: [
      { id: "overview", label: "Dashboard", color: "#059669", icon: icon(<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>) },
    ],
  },
  {
    title: "People",
    tabs: [
      { id: "users", label: "Talent", color: "#8b5cf6", icon: icon(<><path d="M3 18v-6a9 9 0 0118 0v6" /><path d="M21 19a2 2 0 01-2 2h-1v-6h3zM3 19a2 2 0 002 2h1v-6H3z" /></>) },
      { id: "approvals", label: "Approvals", color: "#16a34a", icon: icon(<><circle cx="12" cy="12" r="9" /><path d="M8 12.5l2.7 2.7L16 9.8" /></>) },
      { id: "fans", label: "Fans", color: "#0ea5e9", icon: icon(<><circle cx="9" cy="7" r="4" /><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M23 21v-2a4 4 0 00-3-3.9M16 3.1a4 4 0 010 7.8" /></>) },
      { id: "establishments", label: "Venues", color: "#b45309", icon: icon(<path d="M4 21V5a2 2 0 012-2h8a2 2 0 012 2v16M16 9h2a2 2 0 012 2v10M2 21h20M8 7h4M8 11h4M8 15h4" />) },
      { id: "feedback", label: "Feedback", color: "#0284c7", icon: icon(<path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />) },
    ],
  },
  {
    title: "Money",
    tabs: [
      { id: "orders", label: "Requests", color: "#2563eb", icon: icon(<><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></>) },
      { id: "revenue", label: "Revenue", color: "#059669", icon: icon(<><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" /></>) },
      { id: "costs", label: "Costs", color: "#d97706", icon: icon(<><path d="M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2z" /><path d="M9 8h6M9 12h6" /></>) },
      { id: "allPayouts", label: "All Payouts", color: "#16a34a", icon: icon(<><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /><path d="M6 12h.01M18 12h.01" /></>) },
      { id: "disputes", label: "Disputes", color: "#f59e0b", icon: icon(<path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0zM12 9v4M12 17h.01" />) },
      { id: "paymentIssues", label: "Payment Issues", color: "#dc2626", icon: icon(<><path d="M4 22V4" /><path d="M4 4h13l-2 4 2 4H4" /></>) },
    ],
  },
  {
    title: "Growth",
    tabs: [
      { id: "growth", label: "Growth Funnel", color: "#7c3aed", icon: icon(<path d="M3 4h18l-7 8.5V19l-4 2v-8.5L3 4z" />) },
      { id: "health", label: "Health", color: "#e11d48", icon: icon(<path d="M22 12h-4l-3 9L9 3l-3 9H2" />) },
      { id: "countries", label: "Countries", color: "#0d9488", icon: icon(<><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 010 20M12 2a15 15 0 000 20" /></>) },
      { id: "trending", label: "Trending", color: "#f97316", icon: icon(<path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.07-2.14-.22-4.05 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.15.43-2.29 1-3a2.5 2.5 0 002.5 2.5z" />) },
      { id: "ambassadors", label: "Ambassadors", color: "#6366f1", icon: icon(<><path d="M19 5L5 19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></>) },
    ],
  },
  {
    title: "Team",
    tabs: [
      { id: "admins", label: "Admins", color: "#64748b", icon: icon(<><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></>) },
      { id: "guardrails", label: "Guardrails", color: "#3b82f6", icon: icon(<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />) },
    ],
  },
];

export const TABS: AdminNavTab[] = NAV_GROUPS.flatMap((g) => g.tabs);
